// Approval progress card on claim and variation pages (V0.4.3): where it is (SWI review → SWI approved → Client
// approved → Paid / Complete), one click for "SWI approved (Ready to proceed)" and "Client approved" (with the date
// Summation Partners confirmed), work progress + "Tick complete" on variations, and the payments assigned to it from
// the payment log. Paid is never typed here — it comes from assigning a payment-log line.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { fmtDate, fmtMoney } from '../../lib/format';
import { progressOf, swiApproved, WORK, type ProgressRow, type WorkKey } from '../../lib/progress';
import { Meter, Strip } from '../MoneySummary';
import { Button } from '../kit';

type PayLine = { id: string; amount: number; ref: string | null; payee_raw: string | null; assigned_kind: string | null; assigned_at: string | null; instruction_id: string };

export function ApprovalProgress({
  item,
  canEdit,
  internal,
  update,
}: {
  item: ProgressRow & { id: string; data: Record<string, unknown> | null };
  canEdit: boolean;
  internal: boolean;
  update: (values: Record<string, unknown>, summary: string) => Promise<boolean>;
}) {
  const claim = item.type === 'claim';
  const p = progressOf(item);
  useEffect(() => {
    if (window.location.hash === '#approval') document.getElementById('approval')?.scrollIntoView({ block: 'center' });
  }, []);
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [askDate, setAskDate] = useState(false);
  const lines = useRows<PayLine>(
    () =>
      internal
        ? supabase.from('payment_lines').select('id, amount, ref, payee_raw, assigned_kind, assigned_at, instruction_id').eq('item_id', item.id).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null } as never),
    [item.id, internal],
    ['payment_lines'],
    `item_id=eq.${item.id}`,
  );
  const swiFromSheet = !item.swi_approved_at && String(item.data?.['Ready to Proceed'] ?? '') === 'Yes';
  const ended = p.step === 'paid' || p.step === 'complete' || p.step === 'rejected';
  const steps = claim
    ? ['SWI review', 'SWI approved', 'Client approved', 'Paid']
    : ['SWI review', 'SWI approved', 'Client approved', 'Complete'];
  const reached = { review: 0, hold: 0, swi: 1, client: 2, paid: 3, complete: 3, rejected: -1 }[p.step];
  const paid = lines.rows.reduce((t, l) => t + Number(l.amount), 0);

  return (
    <section className="card appr-card" id="approval" aria-label="Approval progress">
      <div className="appr-head">
        <span className="k">Approval progress</span>
        <span className={`pchip s-${p.step}`}>
          {['swi', 'client', 'paid', 'complete'].includes(p.step) ? '✓ ' : ''}
          {p.label}
        </span>
        {p.cert === 'cert' ? <span className="ptag cert">Cert ✓</span> : p.cert === 'for' ? <span className="ptag cert">For cert</span> : null}
        {p.stale ? <span className="ptag paidq">{claim ? 'Paid? · over 90 days' : 'With client over 90 days'}</span> : null}
      </div>
      <ol className="appr-steps">
        {steps.map((s, i) => (
          <li key={s} className={i < reached ? 'done' : i === reached ? 'now' : ''}>
            <span className="dot" aria-hidden="true" />
            <span className="lbl">{s}</span>
            <span className="when">
              {i === 1 && item.swi_approved_at ? fmtDate(item.swi_approved_at) : i === 1 && swiFromSheet ? 'Ready to proceed (Smartsheet)' : ''}
              {i === 2 && item.client_approved_at ? fmtDate(item.client_approved_at) : ''}
              {i === 3 && claim && item.paid_on ? fmtDate(item.paid_on) : ''}
            </span>
          </li>
        ))}
      </ol>
      <Strip step={p.step} />

      {canEdit && !ended ? (
        <div className="appr-actions">
          {!swiApproved(item) && p.step !== 'client' ? (
            <Button kind="primary" onClick={() => void update({ swi_approved_at: new Date().toISOString() }, 'SWI approved — ready to proceed')}>
              ✓ SWI approved (ready to proceed)
            </Button>
          ) : item.swi_approved_at && p.step === 'swi' ? (
            <Button kind="ghost" onClick={() => void update({ swi_approved_at: null }, 'SWI approval taken back')}>
              Take back SWI approval
            </Button>
          ) : null}
          {p.step === 'swi' || p.step === 'review' ? (
            askDate ? (
              <span className="appr-date">
                <label>
                  Client approved on <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </label>
                <Button
                  kind="primary"
                  onClick={async () => {
                    if (await update(claim ? { client_approved_at: date } : { client_approved_at: date, status: 'approved' }, `Client approved (${date})`)) setAskDate(false);
                  }}
                >
                  Save
                </Button>
                <Button kind="ghost" onClick={() => setAskDate(false)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button onClick={() => setAskDate(true)}>✓ Client approved…</Button>
            )
          ) : item.client_approved_at && p.step === 'client' ? (
            <Button kind="ghost" onClick={() => void update({ client_approved_at: null }, 'Client approval taken back')}>
              Take back client approval
            </Button>
          ) : null}
          <span className="muted small">{claim ? 'Paid comes from the payment log.' : 'Client approved = when Summation Partners confirm.'}</span>
        </div>
      ) : null}

      {!claim && (p.step === 'client' || p.step === 'complete') ? (
        <div className="appr-work">
          <span className="lbl">Work progress</span>
          <div className="work-pick" role="radiogroup" aria-label="Work progress">
            {WORK.map((w) => (
              <button
                key={w.key}
                type="button"
                role="radio"
                aria-checked={p.work === w.key}
                disabled={!canEdit}
                className={p.work === w.key ? 'on' : ''}
                onClick={() => void update({ work_progress: w.key as WorkKey }, `Work progress → ${w.label}`)}
              >
                <Meter q={w.q} />
                {w.label}
              </button>
            ))}
          </div>
          {canEdit && p.step === 'client' ? (
            <Button kind={p.work === 'full' ? 'primary' : 'secondary'} onClick={() => void update({ status: 'complete', stage: 'complete' }, 'Variation ticked complete')}>
              ✓ Tick complete
            </Button>
          ) : null}
          {canEdit && p.step === 'complete' ? (
            <Button kind="ghost" onClick={() => void update({ status: 'approved', stage: null }, 'Variation reopened (not complete)')}>
              Reopen
            </Button>
          ) : null}
        </div>
      ) : null}

      {internal && (lines.rows.length || item.paid_total) ? (
        <div className="appr-paid">
          <span className="lbl">Payments</span>
          {lines.rows.map((l) => (
            <span key={l.id} className="pay-line">
              {fmtMoney(l.amount)} · {l.payee_raw ?? '—'}
              {l.ref ? ` · ${l.ref}` : ''}
              {l.assigned_kind === 'part' ? ' · part payment' : ''}
            </span>
          ))}
          {!claim && item.amount ? <span className="muted small">{p.paidPct ?? 0} % of {fmtMoney(item.amount)} paid ({fmtMoney(paid)})</span> : null}
          <Link to="/payments" className="small">
            Payment log →
          </Link>
        </div>
      ) : null}
    </section>
  );
}
